import React, { useEffect, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { t } from "../i18n/translations";
import { api, formatApiError } from "../lib/api";
import { MapPin, Phone, Mail, Clock, Check } from "lucide-react";
import { toast } from "sonner";

export default function Standort() {
  const { lang } = useLang();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})); }, []);

  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const wh = settings.working_hours || {};
  const locations = [
    { name: "Sarmenstorf", address: "Obere Feldeggstr. 13, 5614 Sarmenstorf, Schweiz" },
    { name: "Rupperswil", address: "Aarestrasse 20, 5102 Rupperswil, Schweiz" },
  ];
  const mapsHref = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  const mapEmbed = (addr) => `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, language: lang });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="standort-page">
      <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">— {t(lang, "standort.title")}</div>
      <h1 className="font-display text-5xl lg:text-6xl text-ink leading-tight max-w-3xl">{t(lang, "standort.subtitle")}</h1>

      <div className="mt-12 grid lg:grid-cols-12 gap-10">
        {/* Left: details */}
        <div className="lg:col-span-5 space-y-8">
          <Info icon={MapPin} label={t(lang, "standort.address_label")}>
            <div className="space-y-3" data-testid="standort-addresses">
              {locations.map((loc, idx) => (
                <a
                  key={loc.name}
                  href={mapsHref(loc.address)}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-ink hover:text-terracotta"
                  data-testid={`standort-address-${idx}`}
                >
                  <div className="text-xs uppercase tracking-widest text-terracotta">{loc.name}</div>
                  <div className="text-sm mt-0.5">{loc.address.replace(", Schweiz", "")}</div>
                </a>
              ))}
            </div>
          </Info>
          <Info icon={Mail} label={t(lang, "standort.booking_email")}>
            <a href={`mailto:${settings.booking_email || "contact@ayurveda-aargau.ch"}`} className="text-ink hover:text-terracotta" data-testid="standort-booking-email">{settings.booking_email || "contact@ayurveda-aargau.ch"}</a>
          </Info>
          <Info icon={Mail} label={t(lang, "standort.general_email")}>
            <a href={`mailto:${settings.email || "info@ayurveda-aargau.ch"}`} className="text-ink hover:text-terracotta" data-testid="standort-general-email">{settings.email || "info@ayurveda-aargau.ch"}</a>
          </Info>
          {settings.phone && (
            <Info icon={Phone} label={t(lang, "standort.phone_label")}>
              <a href={`tel:${settings.phone}`} className="text-ink hover:text-terracotta" data-testid="standort-phone">{settings.phone}</a>
            </Info>
          )}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
              <h3 className="font-display text-xl text-ink">{t(lang, "standort.hours_label")}</h3>
            </div>
            <ul className="divide-y divide-border border border-border rounded-md overflow-hidden bg-white">
              {days.map((d) => {
                const h = wh[d];
                return (
                  <li key={d} className="flex justify-between items-center px-4 py-2.5 text-sm" data-testid={`hours-${d}`}>
                    <span className="text-ink">{t(lang, `days.${d}`)}</span>
                    <span className="text-ink-muted font-mono">{h && h.length === 2 ? `${h[0]} – ${h[1]}` : t(lang, "common.closed")}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Right: maps + contact */}
        <div className="lg:col-span-7 space-y-8">
          {locations.map((loc, idx) => (
            <div key={loc.name} data-testid={`standort-map-wrap-${idx}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-display text-lg text-ink">{loc.name}</h3>
                <a
                  href={mapsHref(loc.address)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs uppercase tracking-widest text-terracotta hover:text-terracotta-hover"
                  data-testid={`standort-directions-${idx}`}
                >
                  Route planen →
                </a>
              </div>
              <div className="aspect-[16/9] rounded-md overflow-hidden border border-border bg-muted">
                <iframe
                  title={`Google Maps – ${loc.name}`}
                  src={mapEmbed(loc.address)}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`standort-map-${idx}`}
                />
              </div>
            </div>
          ))}

          <div className="bg-white border border-border rounded-md p-8">
            <h3 className="font-display text-2xl text-ink">{t(lang, "standort.send_msg_title")}</h3>
            <p className="text-sm text-ink-muted mt-2">{t(lang, "standort.send_msg_body")}</p>
            {sent ? (
              <div className="mt-6 flex items-center gap-3 text-sage" data-testid="standort-sent">
                <Check className="w-5 h-5" strokeWidth={1.5} /> {t(lang, "standort.sent")}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="standort-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t(lang, "standort.name")} value={form.name} onChange={(v) => setForm({...form, name: v})} testid="standort-name" required />
                  <Field label={t(lang, "standort.email")} type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} testid="standort-email" required />
                </div>
                <Field label={t(lang, "standort.phone")} value={form.phone} onChange={(v) => setForm({...form, phone: v})} testid="standort-phone-input" />
                <Field label={t(lang, "standort.message")} value={form.message} onChange={(v) => setForm({...form, message: v})} textarea testid="standort-message" required />
                <button type="submit" disabled={submitting} className="px-7 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30" data-testid="standort-send">
                  {t(lang, "standort.send")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, label, children }) => (
  <div className="flex gap-4">
    <div className="w-11 h-11 rounded-full bg-sand-soft border border-border flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-xs uppercase tracking-widest text-ink-muted mb-1">{label}</div>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", textarea = false, testid, required = false }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}{required && " *"}</span>
    {textarea ? (
      <textarea data-testid={testid} required={required} value={value} onChange={(e) => onChange(e.target.value)} rows={4} className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    ) : (
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    )}
  </label>
);
